import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { ChevronLeft, ChevronRight, Plus, Pencil } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";
import { SectionLoadError } from "./SectionErrorBoundary";
import {
  useRecentTripsWithSummaries,
  type TripWithInvoiceSummary,
} from "@/hooks/useInvoices";
import { TripPaymentStatusBadge } from "@/components/ui/TripPaymentStatusBadge";
import { FINANCES_PATHS } from "@/lib/financesPaths";

function formatCurrency(value: number): string {
  return (
    "$" +
    value.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
}

function TripCard({ item }: { item: TripWithInvoiceSummary }) {
  const [, setLocation] = useLocation();
  const { tripId, tripName, recordLocator, totalSales, totalCommission, invoiceCount, paymentStatus } = item;

  const hasFinancials = totalSales > 0 || totalCommission > 0 || invoiceCount > 0;
  const financialColor = hasFinancials ? "text-foreground" : "text-orange-500";

  return (
    <Card className="flex flex-col border-2 hover:border-primary/30 transition-colors">
      <CardContent className="p-4 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-2 mb-3">
          <h3 className="text-sm font-bold leading-tight line-clamp-2 min-h-[2.5rem] flex-1">
            {tripName}
          </h3>
        </div>

        {recordLocator && (
          <div className="mb-2">
            <span
              className="text-xs font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded"
              title="Trip ID"
            >
              Trip ID: {recordLocator}
            </span>
          </div>
        )}

        <div className="mb-2">
          <TripPaymentStatusBadge status={paymentStatus} />
        </div>

        <div className="space-y-1.5 flex-1">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Total Sales:</span>
            <span className={`font-semibold ${financialColor}`}>
              {hasFinancials ? formatCurrency(totalSales) : "$0"}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Gross Commission:</span>
            <span className={`font-semibold ${financialColor}`}>
              {hasFinancials ? formatCurrency(totalCommission) : "$0"}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Invoices:</span>
            <span className={`font-semibold ${financialColor}`}>{invoiceCount}</span>
          </div>
        </div>

        {/* AR-210 (TC-40.1): at 1024-1279px a quarter-width card is ~147px inside and
            'Edit Trip' + 'Invoice' need 182px, so the row spilled out of the card.
            It may now wrap; with room to spare (desktop) nothing moves. */}
        <div className="flex flex-wrap items-center gap-2 mt-4 pt-3 border-t">
          <Button
            variant="ghost"
            size="sm"
            className="h-11 px-2 text-xs md:h-7"
            onClick={() => setLocation(FINANCES_PATHS.tripEdit(tripId))}
          >
            <Pencil className="h-3 w-3 mr-1" />
            Edit Trip
          </Button>
          <Button
            variant="default"
            size="sm"
            className="text-xs h-7 px-2.5 ml-auto"
            onClick={() => setLocation(`${FINANCES_PATHS.invoicesNew}?tripId=${tripId}`)}
          >
            <Plus className="h-3 w-3 mr-1" />
            Invoice
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function TripCardSkeleton() {
  return (
    <Card className="border-2">
      <CardContent className="p-4 space-y-3">
        <Skeleton className="h-5 w-3/4" />
        <Skeleton className="h-5 w-16 rounded-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-4 w-1/2" />
        <div className="flex gap-2 pt-3 border-t mt-3">
          <Skeleton className="h-7 w-20" />
          <Skeleton className="h-7 w-20 ml-auto" />
        </div>
      </CardContent>
    </Card>
  );
}

const VISIBLE_CARD_COUNT = 4;

function ScrollableTripCards({
  items,
  headingId,
}: {
  items: TripWithInvoiceSummary[];
  headingId: string;
}) {
  const [api, setApi] = useState<CarouselApi>();
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  useEffect(() => {
    if (!api) return;

    const onSelect = () => {
      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
    };

    onSelect();
    api.on("select", onSelect);
    api.on("reInit", onSelect);

    return () => {
      api.off("select", onSelect);
      api.off("reInit", onSelect);
    };
  }, [api]);

  // AR-210 (TC-04.2): the arrow that was just pressed disables at the end of
  // the list and focus fell to <body>. Hand focus to the other arrow instead.
  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const lastArrowRef = useRef<"prev" | "next" | null>(null);
  useEffect(() => {
    const active = document.activeElement;
    if (active === nextRef.current && !canScrollNext) prevRef.current?.focus();
    else if (active === prevRef.current && !canScrollPrev) nextRef.current?.focus();
    else if ((!active || active === document.body) && (!canScrollNext || !canScrollPrev)) {
      const last = lastArrowRef.current;
      if (last === "next" && !canScrollNext) prevRef.current?.focus();
      if (last === "prev" && !canScrollPrev) nextRef.current?.focus();
    }
  }, [canScrollNext, canScrollPrev]);

  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <h2 id={headingId} className="text-sm font-semibold">
          Your Recently Invoiced Trips
        </h2>
        <div className="flex gap-1">
          <Button
            ref={prevRef}
            variant="outline"
            size="icon"
            className="h-11 w-11 md:h-8 md:w-8"
            disabled={!canScrollPrev}
            onClick={() => { lastArrowRef.current = "prev"; api?.scrollPrev(); }}
            aria-label="Scroll to previous trips"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            ref={nextRef}
            variant="outline"
            size="icon"
            className="h-11 w-11 md:h-8 md:w-8"
            disabled={!canScrollNext}
            onClick={() => { lastArrowRef.current = "next"; api?.scrollNext(); }}
            aria-label="Scroll to next trips"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <Carousel setApi={setApi} opts={{ align: "start" }} className="w-full" aria-labelledby={headingId}>
        <CarouselContent className="-ml-4">
          {items.map((item) => (
            <CarouselItem
              key={item.tripId}
              className="pl-4 basis-full sm:basis-1/2 lg:basis-1/4"
            >
              <TripCard item={item} />
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
    </>
  );
}

export function RecentTripCards() {
  const [, setLocation] = useLocation();
  const { data = [], isLoading, error, refetch } = useRecentTripsWithSummaries();

  if (error) {
    return (
      <SectionLoadError
        sectionName="Recent Trips"
        error={error}
        onRetry={() => refetch()}
      />
    );
  }

  const showScroll = data.length > VISIBLE_CARD_COUNT;

  return (
    <section aria-labelledby="recent-trips-heading">
      {isLoading ? (
        <>
          <h2 id="recent-trips-heading" className="text-sm font-semibold mb-3">
            Your Recently Invoiced Trips
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[0, 1, 2, 3].map((i) => (
              <TripCardSkeleton key={i} />
            ))}
          </div>
        </>
      ) : data.length === 0 ? (
        <>
          <h2 id="recent-trips-heading" className="text-sm font-semibold mb-3">
            Your Recently Invoiced Trips
          </h2>
          <Card>
            <CardContent className="flex flex-col items-center gap-4 p-8 text-center">
              <p className="text-sm text-muted-foreground">
                No trips yet. Create an invoice to get started.
              </p>
              <Button
                size="sm"
                onClick={() => setLocation(FINANCES_PATHS.invoicesNew)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Invoice
              </Button>
            </CardContent>
          </Card>
        </>
      ) : showScroll ? (
        <ScrollableTripCards items={data} headingId="recent-trips-heading" />
      ) : (
        <>
          <h2 id="recent-trips-heading" className="text-sm font-semibold mb-3">
            Your Recently Invoiced Trips
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {data.map((item) => (
              <TripCard key={item.tripId} item={item} />
            ))}
          </div>
        </>
      )}
    </section>
  );
}
